/**
 * Sidebar — Collapsible sidebar with mode-dependent tabs.
 * Ported from Builder-5 UX with Zustand store integration.
 *
 * Tab configuration per mode:
 * - Structure: Sections (component list) | Properties (component editor)
 * - Design:    Brand (colors, fonts)     | Content (content editor)
 * - Export:    Settings (site settings)  | Code (preview)
 */
import { useState, useMemo } from 'react';
import { useBuilderStore } from '@/state/store';
import type { BuilderMode } from '@/state/store';
import { componentMeta } from '@/data/component-meta';
import { ComponentItem } from './ComponentItem';
import { ComponentEditor } from './ComponentEditor';
import { ContentPageEditor } from './ContentPageEditor';
import { SubpageSectionToggles } from './SubpageSectionToggles';
import { DesignMode } from './modes/DesignMode';
import { ExportMode } from './modes/ExportMode';

interface TabDef {
  id: string;
  label: string;
}

function getTabsForMode(mode: BuilderMode): TabDef[] {
  if (mode === 'structure') return [
    { id: 'sections', label: 'Sektionen' },
    { id: 'properties', label: 'Texte' },
  ];
  if (mode === 'design') return [
    { id: 'brand', label: 'Brand' },
    { id: 'content', label: 'Inhalt' },
  ];
  return [
    { id: 'settings', label: 'Einstellungen' },
    { id: 'code', label: 'Code' },
  ];
}

const MODE_LABELS: Record<BuilderMode, string> = {
  structure: 'Struktur',
  design: 'Design',
  export: 'Export',
};

export function Sidebar() {
  const mode = useBuilderStore((s) => s.mode);
  const sidebarTab = useBuilderStore((s) => s.sidebarTab);
  const sidebarCollapsed = useBuilderStore((s) => s.sidebarCollapsed);
  const components = useBuilderStore((s) => s.components);
  const order = useBuilderStore((s) => s.order);
  const activeComponent = useBuilderStore((s) => s.activeComponent);
  const settings = useBuilderStore((s) => s.settings);
  const previewPage = useBuilderStore((s) => s.previewPage);

  const selectComponent = useBuilderStore((s) => s.selectComponent);
  const toggleComponent = useBuilderStore((s) => s.toggleComponent);
  const reorderComponents = useBuilderStore((s) => s.reorderComponents);
  const updateComponent = useBuilderStore((s) => s.updateComponent);
  const updateSettings = useBuilderStore((s) => s.updateSettings);
  const setPreviewPage = useBuilderStore((s) => s.setPreviewPage);
  const setOpenModal = useBuilderStore((s) => s.setOpenModal);
  const setSidebarTab = useBuilderStore((s) => s.setSidebarTab);
  const toggleSidebar = useBuilderStore((s) => s.toggleSidebar);

  const [draggedId, setDraggedId] = useState<string | null>(null);

  const tabs = useMemo(() => getTabsForMode(mode), [mode]);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedId(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) return;
    const dragIndex = order.indexOf(draggedId);
    const hoverIndex = order.indexOf(targetId);
    if (dragIndex !== hoverIndex) {
      reorderComponents(dragIndex, hoverIndex);
    }
  };

  const activeComp = activeComponent ? components[activeComponent] : null;

  // ---- Render helpers for mode content ----

  const renderStructureContent = () => {
    if (sidebarTab === 'properties') {
      if (!activeComp) {
        return (
          <div className="sidebar__section">
            <div className="sidebar__empty">
              <div className="sidebar__emptyIcon">{'\u{1F447}'}</div>
              <p className="sidebar__hint">
                Wähle eine Sektion aus der Liste, um ihre Texte zu bearbeiten.
              </p>
            </div>
          </div>
        );
      }
      return (
        <div className="sidebar__section">
          <div className="sidebar__sectionHeader">
            <div className="sidebar__sectionTitle">
              <h3>{componentMeta[activeComp.type]?.icon} {activeComp.name} bearbeiten</h3>
            </div>
          </div>
          <div className="sidebar__sectionBody">
            <ComponentEditor
              component={activeComp}
              settings={settings}
              onUpdate={(data) => updateComponent(activeComp.id, data)}
            />
          </div>
        </div>
      );
    }

    // sections tab (default) — component list + subpage content
    return (
      <>
        {/* Legal pages: no section toggles, just a hint */}
        {(previewPage === 'impressum' || previewPage === 'datenschutz' || previewPage === 'agb') && (
          <div className="sidebar__section">
            <div className="sidebar__sectionBody">
              <p className="sidebar__hint" style={{ padding: '8px 0' }}>
                {previewPage === 'impressum' ? 'Impressum' : previewPage === 'datenschutz' ? 'Datenschutz' : 'AGB'}-Daten werden unter Export → Einstellungen → Rechtliches konfiguriert.
              </p>
              <button
                type="button"
                className="btn btn--small btn--outline"
                onClick={() => setOpenModal('settings')}
              >
                Einstellungen öffnen
              </button>
            </div>
          </div>
        )}

        {/* Subpage Section Toggles + Content Editor (for content pages, not legal) */}
        {previewPage !== 'index' && previewPage !== 'impressum' && previewPage !== 'datenschutz' && previewPage !== 'agb' && (() => {
          const pageKey = previewPage as 'angebot' | 'methoden' | 'ueberMich';
          const settingsKey = pageKey === 'ueberMich' ? 'pageUeberMich'
            : pageKey === 'methoden' ? 'pageMethoden' : 'pageAngebot';
          const pageData = settings[settingsKey] || {};
          const sections = pageData.sections || {};
          return (
            <>
              <SubpageSectionToggles
                page={pageKey}
                sections={sections}
                onToggle={(key, enabled) => {
                  updateSettings({
                    ...settings,
                    [settingsKey]: {
                      ...pageData,
                      sections: { ...sections, [key]: enabled },
                    },
                  });
                }}
              />
              <ContentPageEditor
                page={pageKey}
                settings={settings}
                onUpdate={updateSettings}
              />
            </>
          );
        })()}

        {/* Index Page: Component List (compact, like subpage toggles) */}
        {previewPage === 'index' && (
          <div className="component-list">
            {order.map((id) => {
              const comp = components[id];
              if (!comp) return null;
              return (
                <ComponentItem
                  key={id}
                  component={comp}
                  isActive={activeComponent === id}
                  onSelect={selectComponent}
                  onToggle={toggleComponent}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, id)}
                />
              );
            })}
          </div>
        )}
      </>
    );
  };

  const renderDesignContent = () => <DesignMode />;

  const renderExportContent = () => <ExportMode />;

  const renderContent = () => {
    if (mode === 'structure') return renderStructureContent();
    if (mode === 'design') return renderDesignContent();
    return renderExportContent();
  };

  return (
    <div className={`sidebar__wrapper ${sidebarCollapsed ? 'is-collapsed' : ''}`}>
      <button
        type="button"
        className="sidebar__toggle"
        onClick={toggleSidebar}
        title={sidebarCollapsed ? 'Sidebar einblenden' : 'Sidebar ausblenden'}
      >
        {sidebarCollapsed ? '\u25B8' : '\u25C2'}
      </button>
      <aside className="sidebar">
        <div className="sidebar__header">
          <div className="sidebar__mode">
            <span className="sidebar__modeLabel">{MODE_LABELS[mode]}</span>
          </div>
          <div className="sidebar__tabs" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`sidebar__tab ${sidebarTab === t.id ? 'is-active' : ''}`}
                onClick={() => setSidebarTab(t.id)}
                role="tab"
              >
                <span className="sidebar__tabLabel">{t.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="sidebar__content">
          {/* Page Tabs — always visible in all modes */}
          <div className="sidebar__pageTabs">
            <button
              type="button"
              className={`sidebar__pageTab ${previewPage === 'index' ? 'is-active' : ''}`}
              onClick={() => { setPreviewPage('index'); selectComponent(null); }}
            >
              Start
            </button>
            {settings.generateAngebot && (
              <button
                type="button"
                className={`sidebar__pageTab ${previewPage === 'angebot' ? 'is-active' : ''}`}
                onClick={() => { setPreviewPage('angebot'); selectComponent(null); }}
              >
                Angebot
              </button>
            )}
            {settings.generateMethoden && (
              <button
                type="button"
                className={`sidebar__pageTab ${previewPage === 'methoden' ? 'is-active' : ''}`}
                onClick={() => { setPreviewPage('methoden'); selectComponent(null); }}
              >
                Methoden
              </button>
            )}
            {settings.generateUeberMich && (
              <button
                type="button"
                className={`sidebar__pageTab ${previewPage === 'ueberMich' ? 'is-active' : ''}`}
                onClick={() => { setPreviewPage('ueberMich'); selectComponent(null); }}
              >
                Über mich
              </button>
            )}
            {settings.generateImpressum && (
              <button
                type="button"
                className={`sidebar__pageTab ${previewPage === 'impressum' ? 'is-active' : ''}`}
                onClick={() => { setPreviewPage('impressum'); selectComponent(null); }}
              >
                Impressum
              </button>
            )}
            {settings.generateDatenschutz && (
              <button
                type="button"
                className={`sidebar__pageTab ${previewPage === 'datenschutz' ? 'is-active' : ''}`}
                onClick={() => { setPreviewPage('datenschutz'); selectComponent(null); }}
              >
                Datenschutz
              </button>
            )}
            {settings.generateAgb && (
              <button
                type="button"
                className={`sidebar__pageTab ${previewPage === 'agb' ? 'is-active' : ''}`}
                onClick={() => { setPreviewPage('agb'); selectComponent(null); }}
              >
                AGB
              </button>
            )}
          </div>
          {renderContent()}
        </div>
      </aside>
    </div>
  );
}
